/**
 * PlaygroundCenter
 *
 * Center pane of the Playground IDE shell. Consumes `usePlaygroundState`
 * via context (PlaygroundTab owns the single hook call so the docked tools
 * pane and the center share state). Renders `PlaygroundMain` directly — the
 * tools sidebar UI lives in the docked `tools` pane instead.
 */
import { useMemo } from "react";
import { Wrench } from "lucide-react";
import { toast } from "@/lib/toast";
import { tolerateUnreadyConnectors } from "@/lib/connector-readiness";
import { EmptyState } from "@/components/ui/empty-state";
import { PlaygroundSkeleton } from "@/components/playground/PlaygroundSkeleton";
import { PlaygroundMain } from "@/components/ui-playground/PlaygroundMain";
import SaveRequestDialog from "@/components/tools/SaveRequestDialog";
import {
  PLAYGROUND_FIRST_RUN_PROMPT,
  usePlaygroundStateContext,
} from "@/components/ui-playground/hooks/use-playground-state";
import type { PlaygroundServerSelectorProps } from "@/components/ActiveServerSelector";
import type { ProjectHostContextDraft } from "@/lib/client-config";
import type { EnsureServersReadyResult } from "@/hooks/use-app-state";
import type { EvalChatHandoff } from "@/lib/eval-chat-handoff";

interface PlaygroundCenterProps {
  activeProjectId?: string | null;
  serverName?: string;
  enableMultiModelChat: boolean;
  onSaveHostContext?: (
    projectId: string,
    hostContext: ProjectHostContextDraft,
  ) => Promise<void>;
  ensureServersReady?: (
    serverNames: string[],
  ) => Promise<EnsureServersReadyResult>;
  playgroundServerSelectorProps?: PlaygroundServerSelectorProps;
  evalChatHandoff?: EvalChatHandoff | null;
  onEvalChatHandoffConsumed?: (id: string) => void;
}

export function PlaygroundCenter({
  activeProjectId = null,
  serverName,
  enableMultiModelChat,
  onSaveHostContext,
  ensureServersReady,
  playgroundServerSelectorProps,
  evalChatHandoff = null,
  onEvalChatHandoffConsumed,
}: PlaygroundCenterProps) {
  const state = usePlaygroundStateContext();

  // A failing connector must not block the composer. `PlaygroundMain` gates
  // every send on `ensureServersReady` reporting the focused connector ready
  // and aborts with "Reauthenticate <name> before sending." when it doesn't —
  // which left a broken connector (an expired Figma token, say) holding the
  // whole playground hostage. The chat request itself only carries connectors
  // whose runtime status is `connected`, so sending anyway just costs the turn
  // that connector's tools. `tolerateUnreadyConnectors` lets the send through
  // and surfaces the real state as a dismissible toast pointing at Connectors,
  // where the connector can be reconnected or switched off for good.
  const tolerantEnsureServersReady = useMemo(
    () =>
      ensureServersReady
        ? tolerateUnreadyConnectors(ensureServersReady, (message, dedupeKey) =>
            toast.warning(message, { id: dedupeKey })
          )
        : undefined,
    [ensureServersReady]
  );

  if (state.loadingState.kind === "skeleton") {
    return (
      <div className="h-full flex flex-col overflow-hidden relative">
        <PlaygroundSkeleton />
      </div>
    );
  }

  if (state.loadingState.kind === "sync-timed-out") {
    return (
      <EmptyState
        icon={Wrench}
        title="Still syncing…"
        description="This is taking longer than expected. Try reloading the page."
      />
    );
  }

  // Intentionally no `no-server` empty state: the Playground falls through to
  // PlaygroundMain so the user sees the chat composer + starter chips and can
  // connect a server from the HostPicker in the header (mirrors the legacy
  // Chat tab behavior). PlaygroundMain already guards server-dependent calls
  // behind `serverName && servers[serverName]?.connectionStatus === "connected"`.

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <PlaygroundMain
        activeProjectId={activeProjectId}
        serverName={serverName || ""}
        syncConversationToUrl
        onSaveHostContext={onSaveHostContext}
        enableMultiModelChat={enableMultiModelChat}
        isExecuting={state.isExecuting}
        executingToolName={state.selectedTool}
        invokingMessage={state.invokingMessage}
        pendingExecution={state.pendingExecution}
        onExecutionInjected={state.handleExecutionInjected}
        onWidgetStateChange={(_toolCallId, widgetState) =>
          state.setWidgetState(widgetState)
        }
        deviceType={state.deviceType}
        onDeviceTypeChange={state.setDeviceType}
        playgroundServerSelectorProps={playgroundServerSelectorProps}
        initialInput={
          state.firstRunComposerSeed
            ? PLAYGROUND_FIRST_RUN_PROMPT
            : undefined
        }
        initialInputTypewriter={state.firstRunComposerSeed}
        blockSubmitUntilServerConnected={state.firstRunComposerSeed}
        ensureServersReady={tolerantEnsureServersReady}
        pulseSubmit={state.firstRunComposerSeed}
        showPostConnectGuide={false}
        onFirstMessageSent={
          state.onboarding.isGuidedPostConnect
            ? () => {
                state.onboarding.completeOnboarding();
              }
            : undefined
        }
        evalChatHandoff={evalChatHandoff}
        onEvalChatHandoffConsumed={onEvalChatHandoffConsumed}
      />

      <SaveRequestDialog
        open={state.savedRequestsHook.saveDialogState.isOpen}
        defaultTitle={state.savedRequestsHook.saveDialogState.defaults.title}
        defaultDescription={
          state.savedRequestsHook.saveDialogState.defaults.description
        }
        onCancel={state.savedRequestsHook.closeSaveDialog}
        onSave={state.savedRequestsHook.handleSaveDialogSubmit}
      />
    </div>
  );
}
